import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect } from 'react';
import { Collapse, Nav } from 'react-bootstrap';
import FeatherIcon from 'feather-icons-react';
import { NavLink, useLocation } from 'react-router-dom';
import { capitalize } from '../../../helpers/utils';
import classNames from 'classnames';
import { useAppContext } from '../../../providers/AppProvider';
import { useNavbarVerticalCollapse } from './NavbarVerticalCollapseProvider';

const NavItem = ({ route, level }) => {
  const {
    config: { isNavbarVerticalCollapsed }
  } = useAppContext();

  const { setOpenItems, openItems } = useNavbarVerticalCollapse();

  return (
    <Nav.Item as="li">
      <NavLink
        to={route.path ? route.path : '#!'}
        className={({ isActive }) =>
          classNames('nav-link', {
            'label-1': level === 1,
            active: isActive && route.path !== '#!'
          })
        }
        onClick={() => {
          // A plain link at the top level closes whatever section was open
          if (level === 1) {
            setOpenItems(openItems.map(() => ''));
          }
        }}
      >
        <div
          className={classNames('d-flex align-items-center', {
            'text-300': !route.active
          })}
        >
          {route.icon ? (
            <>
              <span className="nav-link-icon ">
                {route.iconSet === 'font-awesome' ? (
                  <FontAwesomeIcon icon={route.icon} className="fs-8 mx-1" />
                ) : (
                  <FeatherIcon icon={route.icon} size={16} />
                )}
              </span>
              <span className="nav-link-text-wrapper">
                <span className="nav-link-text">{capitalize(route.name)}</span>
              </span>
            </>
          ) : (
            <span className="nav-link-text ">{capitalize(route.name)}</span>
          )}
        </div>
      </NavLink>
    </Nav.Item>
  );
};

const CollapsableNavItem = ({ route, level }) => {
  const { pathname } = useLocation();
  const { setOpenItems, openItems } = useNavbarVerticalCollapse();
  const {
    config: { isNavbarVerticalCollapsed }
  } = useAppContext();

  // Open the collapse by default if the current path is inside its children
  const openCollapse = (children = []) => {
    const checkLink = (child) => {
      return `${child.path}` === pathname || (child.pages && child.pages.some(checkLink));
    };
    return children.some(checkLink);
  };

  // Whether this section is open is NOT this component's own state — it is
  // `openItems[level]`, shared by every section at this level. That single slot
  // is the whole accordion: it holds one name, so opening Project Management
  // writes over Sales Management and Sales Management closes by itself.
  //
  // The version here kept a private `open` boolean alongside the shared list and
  // only ever wrote to the list, never read it. Each section could therefore
  // only ever open itself, and nothing could close it again — which is why they
  // all stayed open, stacked down the sidebar.
  const open = openItems[level] === route.name;

  const updateOpenItems = (name) => {
    setOpenItems(prev => {
      const updated = [...prev];
      updated[level] = name;

      // Collapse all deeper levels when a new item is opened
      for (let i = level + 1; i < updated.length; i += 1) {
        updated[i] = '';
      }

      return updated;
    });
  };

  useEffect(() => {
    // Open the item if the current path matches any of its children
    if (openCollapse(route.pages)) {
      updateOpenItems(route.name);
    }
  }, [pathname]); // Run when pathname changes

  return (
    <>
      <Nav.Link
        onClick={() => updateOpenItems(open ? '' : route.name)}
        className={classNames('dropdown-indicator', {
          'label-1': level === 1,
          collapsed: !open,
          'text-300': !route.active
        })}
        aria-expanded={open}
      >
        <div className="d-flex align-items-center">
          <div className="dropdown-indicator-icon">
            <FontAwesomeIcon
              icon="caret-right"
              className={classNames({
                'text-300': !route.active
              })}
            />
          </div>
          {level === 1 && (
            <span className="nav-link-icon">
              <FeatherIcon icon={route.icon} size={16} />
            </span>
          )}
          <span className="nav-link-text">{capitalize(route.name)}</span>
        </div>
      </Nav.Link>
      <div
        className={classNames('parent-wrapper', {
          'label-1': level === 1
        })}
      >
        <Collapse in={open} className="nav parent">
          <div>
            {level === 1 && (
              <div className="collapsed-nav-item-title d-none">
                {capitalize(route.name)}
              </div>
            )}
            <NavbarVerticalMenu routes={route.pages || []} level={level + 1} />
          </div>
        </Collapse>
      </div>
    </>
  );
};

// The menu is no longer filtered by role. Everybody sees the whole application;
// what a role may DO is decided by the server (middleware/domainAuth.js) and
// surfaced as a modal when a button is pressed. A hidden menu answered the
// wrong question — it told a salesperson the production screens do not exist,
// rather than that this one action is not theirs to take.
const NavbarVerticalMenu = ({ routes, level }) => {
  return (
    <>
      {routes.map((route) => (
        <div key={route.name}>
          {level === 1 ? (
            <div className="nav-item-wrapper">
              {route.pages ? (
                <CollapsableNavItem route={route} level={level} />
              ) : (
                <NavItem route={route} level={level} />
              )}
            </div>
          ) : (
            <>
              {route.pages ? (
                <CollapsableNavItem route={route} level={level} />
              ) : (
                <NavItem route={route} level={level} />
              )}
            </>
          )}
        </div>
      ))}
    </>
  );
};

export default NavbarVerticalMenu;
